import React from 'react'
import clsx from 'clsx'
import { MAX_FILESIZE } from '@SonarCX/utilities/constants'
import { useAppDispatch } from '@SonarCX/hooks/redux'
import { AddErrorAlert } from '@SonarCX/slices/alert'
import Tooltip from '../../atoms/Tooltip'
import UploadBox from '../UserFiles/UploadBox'

import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import useFileDrop from '@SonarCX/hooks/useFileDrop'
import useStyles from './styles'

const AttachmentIcon = ({ stroke = '#8F8F8F', ...props }) => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    {...props}
  >
    <path
      d='M11.9697 12V15.5C11.9697 17.43 13.5397 19 15.4697 19C17.3997 19 18.9697 17.43 18.9697 15.5V10C18.9697 6.13 15.8397 3 11.9697 3C8.09973 3 4.96973 6.13 4.96973 10V16C4.96973 19.31 7.65973 22 10.9697 22'
      stroke={stroke}
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const Wrapper = React.memo(({ fileLength, children }) => {
  if (!fileLength) {
    return children
  }

  return (
    <Tooltip title='Maximum 5 attachments' placement='top' arrow>
      {children}
    </Tooltip>
  )
})

const UploadFile = ({
  onSetFile,
  fileLength,
  disabled = false,
  name = undefined,
  enablePastingFiles = false,
  multi = false,
  children = undefined,
  icon = undefined,
  singleSet = false,
  pasteSelector = '#agentPage',
  ...props
}) => {
  const { inputFileRef } = useTaskAPI()
  const dispatch = useAppDispatch()
  const [error, setError] = React.useState(null)
  const classes = useStyles({ disabled })

  const handleSetFile = React.useCallback(
    file => {
      onSetFile && onSetFile(file)
    },
    [onSetFile]
  )

  const showError = React.useCallback(
    message => {
      setError({ err: { message } })
      dispatch(
        AddErrorAlert({
          err: { message }
        })
      )
    },
    [dispatch]
  )

  const handleChange = React.useCallback(
    e => {
      const files = e.target.files || e.clipboardData.files

      if (files?.length === 0) return

      if (fileLength) return

      handleSetFile(null)

      // Allows adding all files at once
      if (multi && singleSet) {
        handleSetFile(files)
        return
      }

      if (multi) {
        //FileList doesnt support ES6 array opperations
        for (let i = 0; i < files.length; i++) {
          if (files[i].size <= MAX_FILESIZE) {
            handleSetFile(files[i])
          } else {
            showError(
              `${files[i].name} is too large -- files must be smaller than 100MB.`
            )
          }
        }
        if (error) {
          inputFileRef.current.value = ''
        }
      } else {
        if (files.length === 1) {
          const file = files[0]
          if (file.size <= MAX_FILESIZE) {
            handleSetFile(file)
          } else {
            showError(
              `${file.name} is too large -- files must be smaller than 100MB.`
            )
            inputFileRef.current.value = ''
          }
        }
      }
    },
    [
      fileLength,
      handleSetFile,
      multi,
      singleSet,
      error,
      showError,
      inputFileRef
    ]
  )

  const onDrop = React.useCallback(
    files => {
      for (const file of files) {
        if (file.size <= MAX_FILESIZE) {
          handleSetFile(file)
        } else {
          showError(
            `${file.name} is too large -- files must be smaller than 100MB.`
          )
        }
      }
    },
    [handleSetFile, showError]
  )

  React.useEffect(() => {
    const el = document.querySelector(pasteSelector)
    if (el && enablePastingFiles) {
      el.addEventListener('paste', handleChange)
    }
    return () => {
      if (el && enablePastingFiles) {
        el.removeEventListener('paste', handleChange)
      }
    }
  }, [handleChange, enablePastingFiles, pasteSelector])

  const { hovering } = useFileDrop({
    onDrop
  })

  return (
    <>
      <div>
        <Wrapper fileLength={fileLength}>
          <label
            className={clsx(
              classes.label,
              (fileLength || disabled) && classes.faded
            )}
          >
            {children ?? icon ?? <AttachmentIcon {...props} />}

            {!fileLength && (
              <>
                {name && <span className={classes.name}>{name}</span>}

                <input
                  disabled={disabled}
                  onChange={handleChange}
                  type='file'
                  hidden
                  multiple={multi}
                  ref={inputFileRef}
                />
              </>
            )}
          </label>
        </Wrapper>
      </div>
      {hovering && <UploadBox />}
    </>
  )
}

export default UploadFile
